import { queryOptions } from '@tanstack/react-query';

import { graphqlFetch } from './client';
import { queryKeys } from './keys';
import { graphql } from './gql';
import type {
	GetIssuesOptionsQueryVariables,
	GetArticlesOptionsQueryVariables,
} from './gql/graphql';
import { getQueryCacheOptions } from '@shared/query-client/QueryClient';

/**
 * Per-entity `queryOptions` factories: one place that knows an entity's key,
 * its fetcher, and how its cache policy is derived.
 *
 * Scope note (2026-08-12) — these cover the entities whose fetch is a plain
 * request. `issue` and `article` are deliberately **not** here: their queryFns
 * carry real branching (offline cached-fallback, JWT/Cognito refresh, admin
 * header selection, redirect-on-missing) that belongs to topic 4's auth work,
 * and collapsing it into a factory now would either lose behaviour or bake
 * today's auth quirks into the shared layer. They use `queryKeys` from
 * `./keys` so the key half is already centralized; the fetcher half follows
 * once topic 4 lands. `styles` is listed in the topic-3 plan but has **no
 * query hook in the app at all** today (`GET_STYLES_QUERY` was only ever read
 * by the service-worker round-trip that was removed as dead code), so there is
 * nothing to wrap — don't add a factory with no caller.
 */

export interface CacheArgs {
	isOnline: boolean;
	isAdmin?: boolean;
	ttlMinutes?: number;
}

export type IssuesVariables = GetIssuesOptionsQueryVariables;

export function issuesOptions(variables: IssuesVariables, cache: CacheArgs) {
	return queryOptions({
		queryKey: queryKeys.issues(),
		queryFn: () => graphqlFetch(issuesQueryDocument, variables),
		...getQueryCacheOptions(cache),
	});
}

export function articlesOptions(
	variables: ArticlesVariables,
	cache: CacheArgs,
) {
	const key: Array<string> = ['articles'];
	if (variables.isFeatured) key.push('featured');
	return queryOptions({
		queryKey: key,
		queryFn: () => graphqlFetch(articlesQueryDocument, variables),
		...getQueryCacheOptions(cache),
	});
}

export type ArticlesVariables = GetArticlesOptionsQueryVariables;

export const issuesQueryDocument = graphql(`
	query GetIssuesOptions(
		$sortKey: IssueSortKeys
		$limit: Int
		$reverse: Boolean
		$offset: Int
	) {
		issues(
			sortKey: $sortKey
			limit: $limit
			reverse: $reverse
			offset: $offset
		) {
			id
			name
			description
			features
			cover
			externalUrl
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			index
			isFree
			pages
		}
	}
`);

export const articlesQueryDocument = graphql(`
	query GetArticlesOptions($isFeatured: Boolean) {
		articles(isFeatured: $isFeatured) {
			id
			name
			IssueID
			slug
			thumbnail
			featureImage
			index
			isFeatured
			created
			updated
			role
			features
		}
	}
`);
